// angular
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Params } from '@angular/router';
// services
import { CommentsService } from '@app/core/shared/services/comments.service';
// interfaces & enums
import {
  IAllProductsResponse,
  IFaq,
  IProduct,
} from '../interfaces/product.interface';
import { CommentSourceEnum } from '@app/core/shared/enums/commentSource.enum';
import { ApiSearchTypesEnum } from '@app/core/shared/enums/api-search-types.enum';

@Injectable()
export class ProductsService {
  // states
  public productsList = signal<IProduct[]>([]);
  public allProductsCount = signal<number | null>(null);
  public isProductsLoading = signal<boolean>(false);

  public topProductsList = signal<IProduct[]>([]);

  public businessTopProductsList = signal<IProduct[]>([]);
  public businessTopProductsCount = signal<number>(0);
  public isBusinessProductsLoading = signal<boolean>(false);
  public userBusinessProducts = signal<IProduct[]>([]);

  public productDetails = signal<IProduct | null>(null);
  public isProductsDetailsLoading = signal<boolean>(false);

  // dependencies
  private readonly _httpClient = inject(HttpClient);
  private readonly _commentsService = inject(CommentsService);

  constructor() {}

  // get all products

  public getAllProducts(params: Params) {
    // empty productsList on every new api call
    this.productsList.set([]);
    // set is loading to true
    this.isProductsLoading.set(true);

    // api call
    return this._httpClient
      .get<IAllProductsResponse>(`main/products`, {
        params: {
          ...params,
          search_type: ApiSearchTypesEnum.Product,
        },
      })
      .subscribe({
        next: res => {
          const data = res.data;

          this.allProductsCount.set(res.count);
          this.productsList.set(data);
        },
        error: () => this.isProductsLoading.set(false),
        complete: () => this.isProductsLoading.set(false),
      });
  }

  // get top products
  // public getTopProducts() {
  //   return this._httpClient
  //     .get<IAllProductsResponse>(`main/products?skip=0&take=4`)
  //     .subscribe({
  //       next: res => {
  //         const data = res.data;

  //         this.topProductsList.set(data);
  //       },
  //       // error: () => this._spinner.hide(),
  //       // complete: () => this._spinner.hide(),
  //     });
  // }

  // get business top products
  public getBusinessTopProducts(businessId: string) {
    // set loading state
    this.isBusinessProductsLoading.set(true);

    return this._httpClient
      .get<IAllProductsResponse>(
        `main/businesses/custom-url/${businessId}/products`
      )
      .subscribe({
        next: res => {
          const data = res.data;
          // set data & data count
          this.businessTopProductsList.set(data);
          this.businessTopProductsCount.set(res.count);
        },
        error: () => {
          this.isBusinessProductsLoading.set(false);
        },
        complete: () => {
          this.isBusinessProductsLoading.set(false);
        },
      });
  }

  // get business products by id
  public getUserBusinessProductsById(businessId: string) {
    return this._httpClient
      .get<IProduct[]>(`main/businesses/${businessId}/products/list`)
      .subscribe({
        next: res => {
          // set data & data count
          this.userBusinessProducts.set(res);
        },
        error: () => {
          this.isBusinessProductsLoading.set(false);
        },
        complete: () => {
          this.isBusinessProductsLoading.set(false);
        },
      });
  }

  // get product details
  public getProductDetails(id: string) {
    // set loading to true
    this.isProductsDetailsLoading.set(true);

    return this._httpClient.get<IProduct>(`main/products/${id}`).subscribe({
      next: res => {
        // fetch product comments for both guests and logged-in users
        if (res._id) {
          this._commentsService.getAllComments(CommentSourceEnum.Product, res._id);
        }

        // customize faq data
        const faqData = res?.faq?.map((faq: IFaq, index: number) => ({
          _id: index.toString(),
          question: faq.question,
          answer: faq.answer,
        }));

        // initialize final customized data
        const customizedData = {
          ...res,
          faq: faqData,
        };

        this.productDetails.set(customizedData);
      },
      // error: () => this._spinner.hide(),
      complete: () => this.isProductsDetailsLoading.set(false),
    });
  }
}
